import React, { StrictMode, useState } from 'react'
import ReactDOM from 'react-dom'
import '@fs/react-scripts/polyfills'
import * as Sentry from '@sentry/browser'
import { addTranslations, essentialLocales, setAppLocales } from '@fs/zion-locale'
import { ConditionalFooterContext } from '@fs/zion-record-search'
import Root from '@fs/zion-root'
import { Router } from '@fs/zion-router'
import { appPath, sentryDSN, mergeSentryConfig } from '@fs/zion-config'
import { CaptureConsole } from '@sentry/integrations'
import { Integrations as TracingIntegrations } from '@sentry/tracing'

import { AppLayout } from '@fs/zion-ui'
import { FeatureFlagsProvider } from '@fs/zion-flags'
import App from './App'
import * as serviceWorker from './serviceWorker'
import translations from './locales'
import addServerExSupport from './lib/serverExSupport'

// Enable local feature flag development using the file "dev.flags.js"
// If that file exports anything truthy it will initialize feature flags in localhost mode
// and use the flags defined in that file, instead of flags defined in split.
const mockFlags = process.env.NODE_ENV === 'development' ? import('./dev.flags').then((m) => m.default) : undefined

// initialize Sentry for the app
if (sentryDSN) {
  // pass Sentry overrides to mergeSentryConfig
  Sentry.init(
    mergeSentryConfig({
      dsn: sentryDSN,
      denyUrls: [
        // Ignore errors from saved pages
        'file:///',
      ],
      ignoreErrors: [
        // This error happens on beta when we try to use a restricted digitally born image
        "getBornDigitalContent can't identify digital-content because we caught err",
        // Happens in WebKit, particularly iOS Mail. Unresolved WebKit issue: https://bugs.webkit.org/show_bug.cgi?id=197050
        /UnknownError.*Indexed Database server.*/,
        // This error happens when a browser aborts a request
        'Request aborted',
        // We have some off-page dependencies that seem to fail occasionally
        /(fbq|gtag) is not defined/,
        /Can't find variable: (fbq|gtag)/,
        // This should be fixed in zion-flags v2.1.1, but we are somehow still getting the errors
        /No split available for [a-zA-Z0-9_]+/,
      ],
      integrations: [
        new CaptureConsole({
          levels: ['error'],
        }),
        new TracingIntegrations.BrowserTracing(),
      ],
      sampleRate: 0.25,
      // This reduces how many transactions are sent to Sentry. search-react sends more transactions that other projects
      tracesSampleRate: 0.0005,
    })
  )
}

// Consistent scroll restoration behavior across browsers
window.history.scrollRestoration = 'auto'

// For details about loading translations: https://www.familysearch.org/frontier/docs/develop/i18n
addTranslations(translations)

setAppLocales(essentialLocales)

// This is where you pass data from the server to the client using the SERVER_DATA global.
// Here we pass the mounted app path from the server to the client.
const base = appPath ? new URL(appPath).pathname : ''

const FrontierRoot = () => {
  const [showFooter, setShowFooter] = useState(true)
  return (
    <StrictMode>
      <FeatureFlagsProvider mockFlags={mockFlags}>
        <Router basename={base}>
          <ConditionalFooterContext.Provider value={setShowFooter}>
            <Root analytics footer={{ hidden: !showFooter }}>
              <AppLayout fullWidth>
                <App />
              </AppLayout>
            </Root>
          </ConditionalFooterContext.Provider>
        </Router>
      </FeatureFlagsProvider>
    </StrictMode>
  )
}

// We need to be able to set server side experiment headers for requests that are made by the browser.
// This global intercept is designed for that specific purpose.
addServerExSupport(['dasProxy'], ['das.prod.proxy,dz.prod.proxy'])

ReactDOM.render(<FrontierRoot />, document.getElementById('root'))

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister()
